import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { getMyWalletBalance, getMyWalletTransactions } from '../services/api';
import { format } from 'date-fns';
import { useSocket } from '../context/SocketContext';
import { useNavigate } from 'react-router-dom';
import { DocumentTextIcon } from '@heroicons/react/24/outline'; // ✅ Use DocumentTextIcon

const AttendeeWallet = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [balance, setBalance] = useState(0);
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({ type: '', startDate: '', endDate: '' });
    const [pagination, setPagination] = useState({ page: 1, total: 0, totalPages: 0 });
    const socket = useSocket();

    useEffect(() => {
        if (!socket) return;

        if (user?._id) {
            socket.emit('register', user._id);
        }

        const handleWalletUpdate = (data) => {
            console.log('Wallet updated:', data);
            fetchBalance();
            fetchTransactions();
        };

        socket.on('walletUpdated', handleWalletUpdate);

        return () => {
            socket.off('walletUpdated', handleWalletUpdate);
        };
    }, [socket, user?._id]);

    const fetchBalance = async () => {
        try {
            const res = await getMyWalletBalance();
            setBalance(res.data.balance);
        } catch (error) {
            console.error('Failed to fetch balance:', error);
        }
    };

    const fetchTransactions = async (page = 1) => {
        setLoading(true);
        try {
            const params = { ...filters, page, limit: 20 };
            const res = await getMyWalletTransactions(params);
            setTransactions(res.data.transactions);
            setPagination({
                page: res.data.page,
                total: res.data.total,
                totalPages: res.data.totalPages
            });
        } catch (error) {
            console.error('Failed to fetch transactions:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchBalance();
        fetchTransactions();
    }, []);

    useEffect(() => {
        fetchTransactions();
    }, [filters]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters({ ...filters, [name]: value });
    };

    const handleViewBill = (transaction) => {
        console.log('🔍 Transaction clicked:', transaction);
        
        // Check multiple possible locations for orderId
        const orderId = transaction.metadata?.orderId || transaction.orderId || transaction.reference;
        
        if (!orderId) {
            console.warn('⚠️ No order ID found for this transaction');
            alert('No receipt available for this transaction.');
            return;
        }
        
        console.log('✅ Order ID found:', orderId);
        // ✅ Navigate to bill page
        navigate(`/bill/${orderId}`);
    };

    return (
        <div className="font-['Barlow'] space-y-6">
            <h1 className="text-2xl font-normal leading-9 text-[#2b3445]">My Wallet</h1>

            {/* Balance Card */}
            <div className="bg-white rounded-[15px] border border-[#dae1e7] shadow-[0px_1px_3px_0px_rgba(3,0,71,0.09)] p-6 max-w-md">
                <p className="text-sm font-semibold text-[#708090] uppercase tracking-wide">Current Balance</p>
                <p className="text-3xl font-normal text-[#2b3445] mt-1">₹{balance.toFixed(2)}</p>
                <p className="text-sm text-[#708090] mt-2">{user?.name} ({user?.email})</p>
            </div>

            {/* Transaction History */}
            <div>
                <h2 className="text-lg font-semibold text-[#2b3445] mb-4">Transaction History</h2>

                <div className="bg-white rounded-[15px] border border-[#dae1e7] shadow-[0px_1px_3px_0px_rgba(3,0,71,0.09)] p-6">
                    {/* Filters */}
                    <div className="flex flex-wrap gap-4 mb-4">
                        <select
                            name="type"
                            value={filters.type}
                            onChange={handleFilterChange}
                            className="border border-[#dae1e7] rounded-[8px] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1471b9]/20"
                        >
                            <option value="">All Types</option>
                            <option value="credit">Credit</option>
                            <option value="debit">Debit</option>
                        </select>
                        <input
                            type="date"
                            name="startDate"
                            value={filters.startDate}
                            onChange={handleFilterChange}
                            className="border border-[#dae1e7] rounded-[8px] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1471b9]/20"
                        />
                        <input
                            type="date"
                            name="endDate"
                            value={filters.endDate}
                            onChange={handleFilterChange}
                            className="border border-[#dae1e7] rounded-[8px] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1471b9]/20"
                        />
                    </div>

                    {loading ? (
                        <div className="text-center py-8 text-[#708090]">Loading transactions...</div>
                    ) : (
                        <>
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-[#dae1e7]">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-[#708090] uppercase">Type</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-[#708090] uppercase">Amount</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-[#708090] uppercase">Description</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-[#708090] uppercase">Date</th>
                                            <th className="px-4 py-3 text-center text-xs font-semibold text-[#708090] uppercase">Receipt</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-[#dae1e7]">
                                        {transactions.length === 0 ? (
                                            <tr>
                                                <td colSpan="5" className="px-4 py-6 text-center text-[#708090]">No transactions found</td>
                                            </tr>
                                        ) : (
                                            transactions.map((t) => {
                                                const hasOrderId = t.metadata?.orderId || t.orderId || t.reference;
                                                const isDebit = t.type === 'debit';
                                                
                                                return (
                                                    <tr key={t._id} className="hover:bg-gray-50 transition-colors">
                                                        <td className="px-4 py-3 text-sm capitalize">
                                                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${
                                                                t.type === 'credit' 
                                                                    ? 'bg-green-100 text-green-800' 
                                                                    : 'bg-red-100 text-red-800'
                                                            }`}>
                                                                {t.type}
                                                            </span>
                                                        </td>
                                                        <td className={`px-4 py-3 text-sm font-semibold ${
                                                            t.type === 'credit' 
                                                                ? 'text-green-600' 
                                                                : 'text-red-500'
                                                        }`}>
                                                            {t.type === 'credit' ? '+' : '-'}₹{t.amount.toFixed(2)}
                                                        </td>
                                                        <td className="px-4 py-3 text-sm text-[#708090]">
                                                            {t.description || '-'}
                                                        </td>
                                                        <td className="px-4 py-3 text-sm text-[#708090]">
                                                            {format(new Date(t.createdAt), 'dd MMM yyyy, HH:mm')}
                                                        </td>
                                                        <td className="px-4 py-3 text-center">
                                                            {isDebit && hasOrderId ? (
                                                                <button
                                                                    onClick={() => handleViewBill(t)}
                                                                    className="text-[#1471b9] hover:text-[#0f5a9e] transition-colors inline-flex items-center gap-1"
                                                                    title="View Receipt"
                                                                >
                                                                    <DocumentTextIcon className="w-5 h-5" />
                                                                    <span className="text-xs">View</span>
                                                                </button>
                                                            ) : (
                                                                <span className="text-gray-300 text-xs">—</span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            {pagination.totalPages > 1 && (
                                <div className="flex justify-end gap-2 mt-4">
                                    <button
                                        disabled={pagination.page === 1}
                                        onClick={() => fetchTransactions(pagination.page - 1)}
                                        className="px-3 py-1 border border-[#dae1e7] rounded-[8px] text-sm disabled:opacity-50 hover:bg-[#f6f9fc]"
                                    >
                                        Previous
                                    </button>
                                    <span className="px-3 py-1 text-sm text-[#708090]">
                                        Page {pagination.page} of {pagination.totalPages}
                                    </span>
                                    <button
                                        disabled={pagination.page === pagination.totalPages}
                                        onClick={() => fetchTransactions(pagination.page + 1)}
                                        className="px-3 py-1 border border-[#dae1e7] rounded-[8px] text-sm disabled:opacity-50 hover:bg-[#f6f9fc]"
                                    >
                                        Next
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AttendeeWallet;